import type { QueryResult, SoqlExecuteSuccess } from "@/services/text-to-soql";

const AGGREGATE_FN_PATTERN = /\b(COUNT|SUM|AVG|MIN|MAX)\s*\(/i;
const GROUP_BY_PATTERN = /\bGROUP\s+BY\b/i;

/** Values of a child-relationship subquery share one cell, joined by this. */
const MULTI_VALUE_SEPARATOR = "; ";

/** SOQL allows 5 relationship levels; a few more keeps malformed payloads bounded. */
const MAX_FLATTEN_DEPTH = 10;

/** A bare aggregate (no GROUP BY) returns its value in totalSize, not in records. */
function isBareAggregateQuery(soql: string): boolean {
  return AGGREGATE_FN_PATTERN.test(soql) && !GROUP_BY_PATTERN.test(soql);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A child-relationship subquery — `SELECT Id, (SELECT Name FROM Contacts) ...` —
 * arrives as `{ totalSize, done, records: [...] }`, unlike a parent lookup which
 * is just the related record's fields.
 */
function asChildRecords(
  value: Record<string, unknown>,
): Record<string, unknown>[] | null {
  return Array.isArray(value.records)
    ? (value.records as Record<string, unknown>[])
    : null;
}

/**
 * Flattens one Salesforce record into dotted columns, so `Parent: { Name: "A" }`
 * becomes `Parent.Name: "A"` instead of stringifying to `[object Object]`.
 *
 * Child-relationship rows collapse into a single cell per field, with each
 * child's value joined by `; ` — a flat grid has no room to nest them.
 */
function flattenRecord(
  record: Record<string, unknown>,
  prefix: string,
  out: Map<string, string>,
  depth: number,
): Map<string, string> {
  for (const [key, value] of Object.entries(record)) {
    if (key === "attributes") continue;
    const column = prefix ? `${prefix}.${key}` : key;

    // An empty relationship still claims its column; if a sibling record has the
    // relationship populated, pruneRelationshipStubs() drops this placeholder in
    // favour of the dotted columns.
    if (value == null) {
      out.set(column, "");
      continue;
    }

    if (isPlainObject(value) && depth < MAX_FLATTEN_DEPTH) {
      const children = asChildRecords(value);
      if (children) {
        flattenChildRecords(children, column, out, depth + 1);
        continue;
      }
      const nested = flattenRecord(value, column, new Map(), depth + 1);
      if (nested.size === 0) out.set(column, "");
      else nested.forEach((nestedValue, nestedColumn) => out.set(nestedColumn, nestedValue));
      continue;
    }

    out.set(column, typeof value === "object" ? JSON.stringify(value) : String(value));
  }
  return out;
}

function flattenChildRecords(
  children: Record<string, unknown>[],
  prefix: string,
  out: Map<string, string>,
  depth: number,
): void {
  if (children.length === 0) {
    out.set(prefix, "");
    return;
  }

  const valuesByColumn = new Map<string, string[]>();
  children.forEach((child) => {
    flattenRecord(child, prefix, new Map(), depth).forEach((value, column) => {
      const values = valuesByColumn.get(column) ?? [];
      values.push(value);
      valuesByColumn.set(column, values);
    });
  });

  valuesByColumn.forEach((values, column) => {
    out.set(column, values.join(MULTI_VALUE_SEPARATOR));
  });
}

/**
 * Header text for a column key: `Parent.Name` reads as `Parent Name`. The dotted
 * key stays the row lookup — only the label changes.
 */
export function formatColumnLabel(column: string): string {
  return column.replace(/\./g, " ");
}

/**
 * Drops a bare relationship column (`Parent`) once any record contributed the
 * dotted form (`Parent.Name`) — otherwise a null lookup in the first record
 * would add an always-empty column beside the real ones.
 */
function pruneRelationshipStubs(columns: string[]): string[] {
  return columns.filter(
    (column) => !columns.some((other) => other.startsWith(`${column}.`)),
  );
}

export function normalizeExecuteResult(
  data: SoqlExecuteSuccess,
  executedQuery: string,
): QueryResult {
  const records = data.records ?? [];
  const totalSize = data.totalSize ?? records.length;

  // `SELECT COUNT() FROM Account` comes back as { records: [], totalSize: 8 } —
  // the count exists only in totalSize. Synthesize the row so the grid (and the
  // CSV export, which is built from columns/rows) shows the number.
  if (
    records.length === 0 &&
    totalSize > 0 &&
    isBareAggregateQuery(data.query ?? executedQuery)
  ) {
    return {
      columns: ["count"],
      rows: [{ count: String(totalSize) }],
      totalCount: totalSize,
    };
  }

  const flattened = records.map((record) =>
    flattenRecord(record, "", new Map(), 0),
  );

  // Union across records, in first-seen order: a record with a null lookup
  // contributes fewer columns than one with the relationship populated.
  const seen = new Set<string>();
  flattened.forEach((row) => row.forEach((_value, column) => seen.add(column)));
  const columns = pruneRelationshipStubs([...seen]);

  return {
    columns,
    rows: flattened.map((row) => {
      const cells: Record<string, string> = {};
      columns.forEach((column) => {
        cells[column] = row.get(column) ?? "";
      });
      return cells;
    }),
    totalCount: totalSize,
  };
}
